import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { compose } from 'redux';

import { reloadAllBoards } from 'common/actions/boards';
import { reloadCompany } from 'common/actions/company';
import { reloadCustomPostFields } from 'common/actions/customPostFields';
import { reloadConfiguration as reloadIdeasConfiguration } from 'common/actions/ideasConfiguration';
import AJAX from 'common/AJAX';
import Tooltip from 'common/common/Tooltip';
import { RevenueTimeframes } from 'common/company/RevenueHelpers';
import { AllUsersSegmentID, IdeaFieldTypes } from 'common/constants/ideas';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { ShowToastContext, ToastTypes } from 'common/containers/ToastContainer';
import connect from 'common/core/connect';
import { CustomFieldTypes, CustomFieldTypesList } from 'common/customPostFields/Constants';
import Helmet from 'common/helmets/Helmet';
import UpsellModal from 'common/modals/UpsellModal';
import withAccessControl from 'common/routing/withAccessControl';
import { FieldTypeDataMap } from 'common/subdomain/admin/v2/constants';
import AdminV2CustomFieldModal from 'common/subdomain/admin/v2/modals/AdminV2CustomIdeaFieldModal';
import Tappable from 'common/Tappable';
import { H2, P } from 'common/ui/Text';
import AlertDialog from 'common/uiV3/AlertDialog';
import Button from 'common/uiV3/Button';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';
import { RoutePermissions, testEveryPermission } from 'common/util/permissions';
import withContexts from 'common/util/withContexts';

import 'css/components/subdomain/admin/_AdminCustomPostFieldsSettings.scss';

const Modals = {
  uspell: 'upsell',
  create: 'create',
  update: 'update',
  delete: 'delete',
};

class AdminCustomPostFieldsSettings extends Component {
  static propTypes = {
    company: PropTypes.shape({
      _id: PropTypes.string,
    }),
    customPostFields: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
        name: PropTypes.string,
        type: PropTypes.string,
      }),
    ),
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }),
  };

  state = {
    customPostField: null,
    error: null,
    loading: false,
    modal: null,
  };

  onCreateFormulaField = async (data) => {
    const { reloadCustomPostFields, reloadIdeasConfiguration, showToast } = this.props;

    const segment = data.segmentID === AllUsersSegmentID ? null : data.segmentID;
    const response = await AJAX.post('/api/ideas/createFormulaField', {
      calculationFunction: data.calculationFunction,
      factor: data.factor,
      fieldID: data.fieldID,
      name: data.name,
      segmentID: segment,
    });

    const { error, parsedResponse } = parseAPIResponse(response, {
      isSuccessful: (response) => {
        return !!response.formulaField;
      },
      errors: {
        'field name must be unique': 'There is already a field with this name.',
      },
    });

    if (error) {
      showToast(error.message, ToastTypes.error);
      return null;
    }

    showToast('Field created', ToastTypes.success);
    this.setState({ modal: null });
    reloadCustomPostFields();
    reloadIdeasConfiguration();
    return parsedResponse?.formulaField;
  };

  onCreateScoreField = async (data) => {
    const { reloadCustomPostFields, reloadIdeasConfiguration, showToast } = this.props;
    const response = await AJAX.post('/api/ideas/createScoreField', {
      name: data.name,
      fields: data.fields,
    });

    const { error, parsedResponse } = parseAPIResponse(response, {
      isSuccessful: (response) => {
        return !!response.ideaScoreField;
      },
      errors: {
        'field name must be unique': 'There is already a field with this name.',
      },
    });

    if (error) {
      showToast(error.message, ToastTypes.error);
      return null;
    }

    showToast('Field created', ToastTypes.success);
    this.setState({ modal: null });
    reloadCustomPostFields();
    reloadIdeasConfiguration();
    return parsedResponse?.ideaScoreField;
  };

  onCreateCustomField = async (data) => {
    const { reloadCustomPostFields, reloadIdeasConfiguration, showToast } = this.props;
    const response = await AJAX.post('/api/ideas/createCustomField', {
      name: data.name,
      type: data.type,
      options: data.options,
    });

    const { error, parsedResponse } = parseAPIResponse(response, {
      isSuccessful: (response) => {
        return !!response.customIdeaField;
      },
      errors: {
        'field name must be unique': 'There is already a field with this name.',
      },
    });

    if (error) {
      showToast(error.message, ToastTypes.error);
      return null;
    }

    showToast('Field created', ToastTypes.success);
    this.setState({ modal: null });
    reloadCustomPostFields();
    reloadIdeasConfiguration();
    return parsedResponse?.customIdeaField;
  };

  onCreateField = async (data) => {
    if (data.type === IdeaFieldTypes.formula) {
      await this.onCreateFormulaField(data);
    } else if (data.type === IdeaFieldTypes.score) {
      await this.onCreateScoreField(data);
    } else {
      await this.onCreateCustomField(data);
    }
  };

  onDeleteCustomField = async () => {
    const { customPostField } = this.state;
    const { reloadCustomPostFields, reloadIdeasConfiguration, showToast } = this.props;

    const response = await AJAX.post('/api/ideas/deleteCustomField', {
      customIdeaFieldID: customPostField.name,
    });

    const { error } = parseAPIResponse(response, {
      isSuccessful: isDefaultSuccessResponse,
    });

    if (error) {
      showToast(error.message, ToastTypes.error);
    } else {
      showToast('Field deleted', ToastTypes.success);
      this.setState({ modal: null });
      reloadCustomPostFields();
      reloadIdeasConfiguration();
    }
  };

  onUpdateCustomField = async (fieldData) => {
    const { showToast, reloadCustomPostFields, reloadIdeasConfiguration } = this.props;
    const { customPostField } = this.state;

    const response = await AJAX.post('/api/ideas/updateCustomField', {
      customIdeaFieldID: customPostField.name,
      name: fieldData.name,
      options: fieldData.options,
    });

    const { error } = parseAPIResponse(response, {
      isSuccessful: (response) => {
        return !!response.updatedField;
      },
    });

    if (error) {
      showToast(error.message, ToastTypes.error);
    } else {
      showToast('Field updated successfully', ToastTypes.success);
      this.setState({ modal: null });
      reloadCustomPostFields();
      reloadIdeasConfiguration();
    }
  };

  onRevenueToggle = async () => {
    const {
      reloadCompany,
      company: { revenueTimeframe },
    } = this.props;

    this.setState({ error: null });
    const response = await AJAX.post('/api/company/changeSettings', {
      revenueTimeframe:
        revenueTimeframe === RevenueTimeframes.arr ? RevenueTimeframes.mrr : RevenueTimeframes.arr,
    });

    if (response !== 'success') {
      this.setState({
        error: 'Something went wrong, please try again later.',
      });
      return;
    }

    await reloadCompany();
  };

  renderFieldsTable(fields, { renderDefaultFields = true } = {}) {
    const { company } = this.props;
    const usingArr = company.revenueTimeframe === RevenueTimeframes.arr;

    return (
      <div className="fieldsTable">
        <table>
          <thead>
            <tr>
              <th>Field name</th>
              <th>Type</th>
            </tr>
          </thead>
          <tbody>
            {renderDefaultFields ? (
              <>
                <tr>
                  <td className="spend">
                    {usingArr ? 'ARR' : 'MRR'}
                    <button className="spendTimescale" onClick={this.onRevenueToggle}>
                      Use {usingArr ? 'MRR' : 'ARR'} instead
                    </button>
                    <Tooltip value="Show company spend as ARR instead of&nbsp;MRR">
                      <div className="icon-help" />
                    </Tooltip>
                  </td>
                  <td className="type">Default</td>
                </tr>
                <tr>
                  <td>Category</td>
                  <td className="type">Default</td>
                </tr>
                <tr>
                  <td>ETA</td>
                  <td className="type">Default</td>
                </tr>
              </>
            ) : null}
            {fields.map((customPostField) => {
              const type = FieldTypeDataMap[customPostField.type].label;
              return (
                <tr key={customPostField.name}>
                  <td>{customPostField.label}</td>
                  <td className="type">{customPostField.deletable ? type : 'Default'}</td>
                  {!customPostField.deletable ? null : (
                    <td className="options">
                      <Tappable
                        onTap={() => this.setState({ modal: Modals.update, customPostField })}>
                        <span className="icon icon-pencil" />
                      </Tappable>
                      <Tappable
                        onTap={() => this.setState({ modal: Modals.delete, customPostField })}>
                        <span className="icon icon-x" />
                      </Tappable>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  renderError() {
    const { error } = this.state;
    if (!error) {
      return null;
    }

    return <div className="error">{error}</div>;
  }

  render() {
    const { company, ideaFields } = this.props;
    const { customPostField, loading, modal } = this.state;

    const closeModal = () => {
      this.setState({ modal: null });
    };

    const openCreateModal = () => {
      if (!company.features?.customPostFields) {
        this.setState({ modal: Modals.upsell });
        return;
      }

      this.setState({ modal: Modals.create, customPostField: null });
    };

    const isIDBEnabled = company.featureAllowlist.includes('idea-database');
    const postAndIdeaFields = ideaFields.filter(
      (field) => field.custom && field.type in CustomFieldTypes,
    );
    const ideaOnlyFields = ideaFields.filter(
      (field) => !postAndIdeaFields.some((postField) => postField.name === field.name),
    );

    return (
      <div className="adminCustomPostFieldsSettings">
        <Helmet title={`${isIDBEnabled ? 'Post & Idea Fields' : 'Post Fields'} | Canny`} />
        <div className="content">
          <H2 variant="headingMd">{isIDBEnabled ? 'Post & Idea Fields' : 'Post Fields'}</H2>
          <P className="text">
            You can configure which fields show up on the create post form, posts, and roadmaps.
          </P>
          <div className="fieldsTableContainer">
            <Button onClick={openCreateModal} className="mb-4 self-end">
              Create field
            </Button>
            {this.renderFieldsTable(postAndIdeaFields, { renderDefaultFields: true })}
            {this.renderError()}
          </div>
        </div>
        {isIDBEnabled ? (
          <div className="content">
            <H2 variant="headingMd">Idea-only fields (beta)</H2>
            <P className="text">
              These fields are only supported on ideas. Use these to sort, filter, and prioritize
              ideas.
            </P>
            <div className="fieldsTableContainer">
              {this.renderFieldsTable(ideaOnlyFields, { renderDefaultFields: false })}
            </div>
          </div>
        ) : null}
        {modal === Modals.upsell && (
          <UpsellModal
            cta="Add custom fields to&nbsp;posts"
            feature="customPostFields"
            onClose={() => this.setState({ modal: null })}
            onUpsell={() => this.setState({ modal: Modals.create })}
            show={true}
          />
        )}
        <AdminV2CustomFieldModal
          isOpen={[Modals.create, Modals.update].includes(modal)}
          types={
            company.featureAllowlist.includes('idea-database')
              ? Object.values(IdeaFieldTypes)
              : CustomFieldTypesList
          }
          onClose={closeModal}
          onSubmit={modal === Modals.create ? this.onCreateField : this.onUpdateCustomField}
          loading={loading}
          field={customPostField}
          onDelete={() => this.setState({ modal: Modals.delete })}
        />
        <AlertDialog open={modal === Modals.delete} onOpenChange={closeModal}>
          <AlertDialog.Content onCloseAutoFocus={() => this.setState({ customPostField: null })}>
            <AlertDialog.Header>
              <AlertDialog.Title>Delete this field from all posts?</AlertDialog.Title>
              <AlertDialog.Description>
                The field <span className="font-bold text-base">"{customPostField?.label}"</span>{' '}
                exists on all posts. Deleting it will remove it everywhere and cannot
                be&nbsp;undone.
              </AlertDialog.Description>
            </AlertDialog.Header>
            <AlertDialog.Footer>
              <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
              <AlertDialog.Action asChild color="error">
                <Button onClick={this.onDeleteCustomField}>Delete</Button>
              </AlertDialog.Action>
            </AlertDialog.Footer>
          </AlertDialog.Content>
        </AlertDialog>
      </div>
    );
  }
}

export default compose(
  connect(({ ideasConfiguration }) => ({ ideaFields: ideasConfiguration.data?.fields ?? [] }), {
    reloadBoards: reloadAllBoards,
    reloadCompany,
    reloadCustomPostFields,
    reloadIdeasConfiguration,
  }),
  withAccessControl(
    testEveryPermission(RoutePermissions.adminSettings.postFields['post-fields']),
    '/admin/settings',
    { forwardRef: true },
  ),
  withContexts(
    {
      company: CompanyContext,
      showToast: ShowToastContext,
    },
    {
      forwardRef: true,
    },
  ),
)(AdminCustomPostFieldsSettings);
